/** Pure presentation helpers for the usage screens (phase 6). */
import type { PaywallFunnel, RetentionCohortRow, UsagePoint } from "@apphq/shared/types";
import { dayShort } from "./format";

export const RETENTION_DAYS = [1, 7, 14, 30] as const;
export type RetentionDay = (typeof RETENTION_DAYS)[number];

export interface RetentionCellStyle {
  /** Mix percentage of the accent colour (0-100); null = immature cell, no tint at all. */
  tint: number | null;
  /** White text from a dark tint on. */
  light: boolean;
  text: string;
}

/** Tint stays above 12 so a real 0% is still visible; a null (immature) cell is never tinted and never shows "0%". */
export function retentionCellStyle(value: number | null | undefined): RetentionCellStyle {
  if (value === null || value === undefined || !Number.isFinite(value)) return { tint: null, light: false, text: "–" };
  const v = Math.min(100, Math.max(0, value));
  const tint = Math.round(12 + v * 0.88);
  return { tint, light: tint >= 55, text: `${Math.round(v)}%` };
}

export function cohortValue(row: RetentionCohortRow, day: RetentionDay): number | null {
  return row[`d${day}` as "d1" | "d7" | "d14" | "d30"];
}

/** `2026-10-19` -> `week van 19 okt`. */
export function cohortLabel(start: string): string {
  return `Week van ${dayShort(start)}`;
}

/** True when no cohort has any user yet. */
export function retentionIsEmpty(rows: readonly RetentionCohortRow[]): boolean {
  return rows.every((r) => r.users === 0);
}

/** Newest cohort first (the API sends newest last). */
export function cohortsNewestFirst(rows: readonly RetentionCohortRow[]): RetentionCohortRow[] {
  return [...rows].sort((a, b) => (a.cohort_start < b.cohort_start ? 1 : a.cohort_start > b.cohort_start ? -1 : 0));
}

export interface FunnelStep {
  key: "viewed" | "started" | "completed";
  label: string;
  value: number;
  /** Bar width 0-100 relative to the first step. */
  widthPercent: number;
  /** Step-to-step conversion ratio (0-1), null for the first step or without denominator. */
  rate: number | null;
}

const ratio = (num: number, den: number): number | null => (den > 0 ? num / den : null);

export function funnelIsEmpty(f: PaywallFunnel | null | undefined): boolean {
  return !f || (f.viewed === 0 && f.started === 0 && f.completed === 0);
}

/** Bekeken -> gestart -> betaald. Widths relative to the biggest step so a skewed funnel (more paid than viewed) never overflows. */
export function funnelSteps(f: PaywallFunnel): FunnelStep[] {
  const max = Math.max(f.viewed, f.started, f.completed, 0);
  const width = (n: number) => (max > 0 ? Math.min(100, Math.max(0, (n / max) * 100)) : 0);
  return [
    { key: "viewed", label: "Paywall bekeken", value: f.viewed, widthPercent: width(f.viewed), rate: null },
    { key: "started", label: "Aankoop gestart", value: f.started, widthPercent: width(f.started), rate: f.start_rate ?? ratio(f.started, f.viewed) },
    { key: "completed", label: "Betaald", value: f.completed, widthPercent: width(f.completed), rate: f.paid_rate ?? ratio(f.completed, f.started) },
  ];
}

/** dau / mau; null without mau. Clamped to 1 (a today-only user set can never exceed the month). */
export function stickinessRatio(dau: number, mau: number): number | null {
  if (!(mau > 0)) return null;
  return Math.min(1, Math.max(0, dau / mau));
}

export interface UsageChartPoint {
  date: string;
  label: string;
  dau: number;
}

/** Sorted by day, de-duplicated (last wins). */
export function toUsageChartPoints(points: readonly UsagePoint[]): UsageChartPoint[] {
  const byDay = new Map<string, UsagePoint>();
  for (const p of points) byDay.set(p.date, p);
  return [...byDay.values()]
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .map((p) => ({ date: p.date, label: dayShort(p.date), dau: p.dau }));
}

/** Sessions per active user in the period-wide MAU sense; null without users. */
export function sessionsPerUser(sessions: number, mau: number): number | null {
  return mau > 0 ? sessions / mau : null;
}
